import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Chip, CircularProgress,
  Stack, TextField, MenuItem,
} from '@mui/material';
import { toast } from 'react-toastify';
import { payrollApi } from '../../../api/payrollApi';
import {
  TaxDeclarationDto,
  TaxDeclarationStatus,
  TaxDeclarationStatusLabels,
  TaxDeclarationStatusColors,
} from '../../../types/payrollTypes';

const TaxDeclarationsPage: React.FC = () => {
  const [declarations, setDeclarations] = useState<TaxDeclarationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [financialYear, setFinancialYear] = useState<string>('');

  const loadDeclarations = async () => {
    setLoading(true);
    try {
      const data = await payrollApi.getTaxDeclarations(undefined, financialYear || undefined);
      setDeclarations(data);
    } catch {
      toast.error('Failed to load tax declarations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDeclarations();
  }, [financialYear]);

  const currentYear = new Date().getFullYear();
  const fyOptions = [...Array(5)].map((_, i) => {
    const y = currentYear - 2 + i;
    return `${y}-${y + 1}`;
  });

  const handleStatusChange = async (id: string, status: TaxDeclarationStatus) => {
    try {
      await payrollApi.updateTaxDeclarationStatus(id, status);
      toast.success('Status updated');
      void loadDeclarations();
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Tax Declarations</Typography>
        <TextField
          select
          size="small"
          label="Financial Year"
          value={financialYear}
          onChange={(e) => setFinancialYear(e.target.value)}
          sx={{ width: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {fyOptions.map((fy) => (
            <MenuItem key={fy} value={fy}>{fy}</MenuItem>
          ))}
        </TextField>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Total Records: {declarations.length}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Financial Year</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Declarations</TableCell>
                  <TableCell align="right">Total Declared</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {declarations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No tax declarations found</TableCell>
                  </TableRow>
                ) : (
                  declarations.map((dec) => {
                    const totalDeclared = dec.items.reduce((sum, item) => sum + item.declaredAmount, 0);
                    const canApprove =
                      dec.status === TaxDeclarationStatus.Draft ||
                      dec.status === TaxDeclarationStatus.Submitted;

                    return (
                      <TableRow key={dec.id}>
                        <TableCell>{dec.employeeName}</TableCell>
                        <TableCell>{dec.financialYear}</TableCell>
                        <TableCell>
                          <Chip
                            label={TaxDeclarationStatusLabels[dec.status]}
                            color={TaxDeclarationStatusColors[dec.status]}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{dec.items.length}</TableCell>
                        <TableCell align="right">₹{totalDeclared.toLocaleString('en-IN')}</TableCell>
                        <TableCell align="center">
                          {canApprove ? (
                            <Chip
                              label="Approve"
                              color="success"
                              size="small"
                              clickable
                              onClick={() => handleStatusChange(dec.id, TaxDeclarationStatus.Approved)}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default TaxDeclarationsPage;
